import {
  isIntellioEmailVerified,
  resolveIntellioProviderFromClaims,
} from "@triedge/intellio-google-auth";
import {
  resolveUserPoolIdFromIssuer,
  resolveYstnjAuthAuthority,
  type YstnjAuthAuthority,
} from "../auth/ystnjAuthAuthority";
import {
  YSTNJ_COGNITO_GROUP,
  YSTNJ_RUNTIME_PERSONA,
  parseRuntimePersonaProjection,
  type YstnjCognitoGroup,
} from "../auth/ystnjCognitoGroupVocabulary";
import {
  normalizeCanonicalOrMigrationCognitoGroups,
  parseCanonicalOrMigrationCognitoGroup,
} from "../auth/ystnjCognitoGroupVocabularyCompat";

export type CognitoPersona =
  | "parent"
  | "staff"
  | "administrator"
  | "super-administrator"
  | "guest";

export type CognitoProfile = {
  name?: string;
  email?: string;
  username?: string;
  groups: YstnjCognitoGroup[];
  permissions: string[];
  role?: string;
  persona: CognitoPersona;
  userSub?: string;
  provider?: string;
  emailVerified: boolean;
  isGoogleProvider: boolean;
  isIntellioUser: boolean;
  isSuperAdmin: boolean;
};

export type CognitoFailureReason =
  | "no-authority-matched-token"
  | "token-authority-mismatch"
  | "token-decode-failed";

export type CognitoObservedTokenTuple = {
  userPoolId?: string;
  userPoolClientId?: string;
  tokenKey?: string;
  lastAuthUserKey?: string;
  username?: string;
  authorityMatched: boolean;
};

export type CognitoProfileResolution = {
  profile: CognitoProfile | null;
  expectedTuple: YstnjAuthAuthority;
  observedTokenTuple: CognitoObservedTokenTuple | null;
  failureReason: CognitoFailureReason | null;
  token: string | null;
  tokenKey: string | null;
};

const COGNITO_STORAGE_PREFIX = "CognitoIdentityServiceProvider.";

const normalizeToken = (value: string | null | undefined): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const buildNormalizedArraySignature = (
  values: readonly string[] | null | undefined,
): string => JSON.stringify(
  [...new Set((values ?? []).map((entry) => normalizeToken(entry)).filter(Boolean))].sort(),
);

const buildObservedTokenTupleSignature = (
  tuple: CognitoObservedTokenTuple | null | undefined,
): string => JSON.stringify({
  userPoolId: normalizeToken(tuple?.userPoolId),
  userPoolClientId: normalizeToken(tuple?.userPoolClientId),
  tokenKey: normalizeToken(tuple?.tokenKey),
  lastAuthUserKey: normalizeToken(tuple?.lastAuthUserKey),
  username: normalizeToken(tuple?.username),
  authorityMatched: Boolean(tuple?.authorityMatched),
});

const buildYstnjAuthAuthoritySignature = (
  authority: YstnjAuthAuthority | null | undefined,
): string => JSON.stringify({
  userPoolId: normalizeToken(authority?.userPoolId),
  userPoolClientId: normalizeToken(authority?.userPoolClientId),
  identityPoolId: normalizeToken(authority?.identityPoolId),
  oauthDomain: normalizeToken(authority?.oauthDomain),
  appsyncUrl: normalizeToken(authority?.appsyncUrl),
  redirectSignInUrls: [...(authority?.redirectSignInUrls ?? [])].map(normalizeToken).filter(Boolean).sort(),
  redirectSignOutUrls: [...(authority?.redirectSignOutUrls ?? [])].map(normalizeToken).filter(Boolean).sort(),
  origin: normalizeToken(authority?.origin),
  originSupported: Boolean(authority?.originSupported),
});

export const buildCognitoProfileSignature = (
  profile: CognitoProfile | null | undefined,
): string => JSON.stringify({
  name: normalizeToken(profile?.name),
  email: normalizeToken(profile?.email),
  username: normalizeToken(profile?.username),
  groups: buildNormalizedArraySignature(profile?.groups),
  permissions: buildNormalizedArraySignature(profile?.permissions),
  role: normalizeToken(profile?.role),
  persona: normalizeToken(profile?.persona),
  userSub: normalizeToken(profile?.userSub),
  provider: normalizeToken(profile?.provider),
  emailVerified: Boolean(profile?.emailVerified),
  isGoogleProvider: Boolean(profile?.isGoogleProvider),
  isIntellioUser: Boolean(profile?.isIntellioUser),
  isSuperAdmin: Boolean(profile?.isSuperAdmin),
});

export const areCognitoProfilesEquivalent = (
  left: CognitoProfile | null | undefined,
  right: CognitoProfile | null | undefined,
): boolean => buildCognitoProfileSignature(left) === buildCognitoProfileSignature(right);

export const buildCognitoProfileResolutionSignature = (
  resolution: CognitoProfileResolution | null | undefined,
): string => JSON.stringify({
  profile: buildCognitoProfileSignature(resolution?.profile),
  expectedTuple: buildYstnjAuthAuthoritySignature(resolution?.expectedTuple),
  observedTokenTuple: buildObservedTokenTupleSignature(resolution?.observedTokenTuple),
  failureReason: normalizeToken(resolution?.failureReason),
  tokenKey: normalizeToken(resolution?.tokenKey),
});

export const areCognitoProfileResolutionsEquivalent = (
  left: CognitoProfileResolution | null | undefined,
  right: CognitoProfileResolution | null | undefined,
): boolean => (
  buildCognitoProfileResolutionSignature(left)
  === buildCognitoProfileResolutionSignature(right)
);

const normalizePersonaCandidate = (
  value: string | null | undefined,
): Exclude<CognitoPersona, "guest"> | null => {
  if (!value) {
    return null;
  }
  const runtimePersona = parseRuntimePersonaProjection(value);
  if (runtimePersona === YSTNJ_RUNTIME_PERSONA.PARENT) {
    return YSTNJ_RUNTIME_PERSONA.PARENT;
  }
  if (runtimePersona === YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR) {
    return YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR;
  }
  if (runtimePersona === YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR) {
    return YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR;
  }
  if (runtimePersona === YSTNJ_RUNTIME_PERSONA.STAFF) {
    return YSTNJ_RUNTIME_PERSONA.STAFF;
  }

  const canonicalGroup = parseCanonicalOrMigrationCognitoGroup(value);
  if (canonicalGroup === YSTNJ_COGNITO_GROUP.PARENT) {
    return YSTNJ_RUNTIME_PERSONA.PARENT;
  }
  if (canonicalGroup === YSTNJ_COGNITO_GROUP.SUPER_ADMINISTRATOR) {
    return YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR;
  }
  if (
    canonicalGroup === YSTNJ_COGNITO_GROUP.ADMIN
    || canonicalGroup === YSTNJ_COGNITO_GROUP.OWNER
  ) {
    return YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR;
  }
  if (
    canonicalGroup === YSTNJ_COGNITO_GROUP.STAFF
    || canonicalGroup === YSTNJ_COGNITO_GROUP.EDITOR
    || canonicalGroup === YSTNJ_COGNITO_GROUP.NURSE
  ) {
    return YSTNJ_RUNTIME_PERSONA.STAFF;
  }
  return null;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }
  const payload = parts[1];
  if (!payload) {
    return null;
  }
  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const resolveUserPoolClientIdFromPayload = (
  payload: Record<string, unknown> | null,
): string => {
  if (!payload) {
    return "";
  }
  return typeof payload.aud === "string" ? payload.aud.trim() : "";
};

const resolveStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage;
};

const collectStorageKeys = (storage: Storage): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key) {
      keys.push(key);
    }
  }
  return keys;
};

const extractClientIdFromStorageKey = (key: string): string | null => {
  if (!key.startsWith(COGNITO_STORAGE_PREFIX)) {
    return null;
  }
  const remainder = key.slice(COGNITO_STORAGE_PREFIX.length);
  const [clientId] = remainder.split(".");
  return clientId?.trim() || null;
};

type TokenCandidate = {
  key: string;
  token: string;
  payload: Record<string, unknown> | null;
  userPoolId: string;
  userPoolClientId: string;
  authorityMatched: boolean;
};

const buildObservedTokenTuple = (
  candidate: TokenCandidate | null,
  extras?: {
    lastAuthUserKey?: string | null;
    username?: string | null;
  },
): CognitoObservedTokenTuple | null => {
  if (!candidate && !extras?.lastAuthUserKey && !extras?.username) {
    return null;
  }

  return {
    userPoolId: candidate?.userPoolId || undefined,
    userPoolClientId: candidate?.userPoolClientId || undefined,
    tokenKey: candidate?.key || undefined,
    lastAuthUserKey: extras?.lastAuthUserKey || undefined,
    username: extras?.username || undefined,
    authorityMatched: Boolean(candidate?.authorityMatched),
  };
};

const buildTokenCandidates = (
  storage: Storage,
  authority: YstnjAuthAuthority,
): TokenCandidate[] =>
  collectStorageKeys(storage)
    .filter((key) => key.endsWith(".idToken"))
    .map((key) => {
      const token = storage.getItem(key);
      if (!token) {
        return null;
      }
      const payload = decodeJwtPayload(token);
      const userPoolId = resolveUserPoolIdFromIssuer(payload?.iss);
      const userPoolClientId = resolveUserPoolClientIdFromPayload(payload);
      return {
        key,
        token,
        payload,
        userPoolId,
        userPoolClientId,
        authorityMatched:
          userPoolId === authority.userPoolId
          && userPoolClientId === authority.userPoolClientId,
      } satisfies TokenCandidate;
    })
    .filter((candidate): candidate is TokenCandidate => Boolean(candidate));

type IdTokenResolution = {
  authority: YstnjAuthAuthority;
  token: string | null;
  tokenKey: string | null;
  observedTokenTuple: CognitoObservedTokenTuple | null;
  failureReason: CognitoFailureReason | null;
};

const readIdTokenResolution = (): IdTokenResolution => {
  const authority = resolveYstnjAuthAuthority();
  const storage = resolveStorage();
  if (!storage) {
    return {
      authority,
      token: null,
      tokenKey: null,
      observedTokenTuple: null,
      failureReason: null,
    };
  }

  const lastAuthUserKey = authority.userPoolClientId
    ? `${COGNITO_STORAGE_PREFIX}${authority.userPoolClientId}.LastAuthUser`
    : null;
  const username = lastAuthUserKey ? storage.getItem(lastAuthUserKey) : null;
  const preferredTokenKey =
    authority.userPoolClientId && username
      ? `${COGNITO_STORAGE_PREFIX}${authority.userPoolClientId}.${username}.idToken`
      : null;

  const candidates = buildTokenCandidates(storage, authority);
  const preferredCandidate = preferredTokenKey
    ? candidates.find((candidate) => candidate.key === preferredTokenKey) ?? null
    : null;

  if (preferredCandidate?.authorityMatched) {
    return {
      authority,
      token: preferredCandidate.token,
      tokenKey: preferredCandidate.key,
      observedTokenTuple: buildObservedTokenTuple(preferredCandidate, {
        lastAuthUserKey,
        username,
      }),
      failureReason: null,
    };
  }

  const matchedCandidate =
    candidates.find((candidate) => candidate.authorityMatched) ?? null;
  if (matchedCandidate) {
    return {
      authority,
      token: matchedCandidate.token,
      tokenKey: matchedCandidate.key,
      observedTokenTuple: buildObservedTokenTuple(matchedCandidate, {
        lastAuthUserKey,
        username,
      }),
      failureReason: null,
    };
  }

  if (preferredCandidate) {
    return {
      authority,
      token: null,
      tokenKey: null,
      observedTokenTuple: buildObservedTokenTuple(preferredCandidate, {
        lastAuthUserKey,
        username,
      }),
      failureReason: preferredCandidate.payload
        ? "token-authority-mismatch"
        : "token-decode-failed",
    };
  }

  const firstCandidate = candidates[0] ?? null;
  if (!firstCandidate) {
    return {
      authority,
      token: null,
      tokenKey: null,
      observedTokenTuple: buildObservedTokenTuple(null, {
        lastAuthUserKey,
        username,
      }),
      failureReason: null,
    };
  }

  return {
    authority,
    token: null,
    tokenKey: null,
    observedTokenTuple: buildObservedTokenTuple(firstCandidate, {
      lastAuthUserKey,
      username,
    }),
    failureReason: firstCandidate.payload
      ? "no-authority-matched-token"
      : "token-decode-failed",
  };
};

const resolveGroupsFromPayload = (
  payload: Record<string, unknown> | null,
): YstnjCognitoGroup[] => {
  if (!payload) {
    return [];
  }
  const groups: string[] = [];
  const rawGroups = payload["cognito:groups"];
  if (Array.isArray(rawGroups)) {
    groups.push(...rawGroups.map(String));
  } else if (typeof rawGroups === "string") {
    groups.push(...rawGroups.split(",").map((entry) => entry.trim()));
  }
  return normalizeCanonicalOrMigrationCognitoGroups(groups);
};

const resolvePermissionsFromPayload = (
  payload: Record<string, unknown> | null,
): string[] => {
  if (!payload) {
    return [];
  }
  const values = [
    payload["triedge:permissions"],
    payload["custom:permissions"],
    payload.permissions,
    payload.permission,
    payload.scp,
    payload.scope,
  ];
  const permissions = values.flatMap((value) => {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value.map(String);
    }
    if (typeof value === "string") {
      return value.split(/[,\s]+/);
    }
    return [];
  });
  return [
    ...new Set(
      permissions.map((entry) => entry.trim().toLowerCase()).filter(Boolean),
    ),
  ];
};

const resolveEmailFromPayload = (
  payload: Record<string, unknown> | null,
): string | null => {
  if (!payload) {
    return null;
  }
  const email =
    payload.email ??
    payload["cognito:username"] ??
    payload.preferred_username ??
    payload.username;
  return typeof email === "string" && email.length > 0 ? email : null;
};

const resolveUsernameFromPayload = (
  payload: Record<string, unknown> | null,
): string | null => {
  if (!payload) {
    return null;
  }
  const username =
    payload["cognito:username"] ??
    payload.preferred_username ??
    payload.username;
  return typeof username === "string" && username.length > 0 ? username : null;
};

const resolveUserSubFromPayload = (
  payload: Record<string, unknown> | null,
): string | null => {
  if (!payload) {
    return null;
  }
  return typeof payload.sub === "string" ? payload.sub : null;
};

const resolveProviderFromPayload = (
  payload: Record<string, unknown> | null,
): string | null => {
  if (!payload) {
    return null;
  }
  const provider = resolveIntellioProviderFromClaims(payload);
  return provider === "google" ? "Google" : provider;
};

const resolveRoleFromPayload = (
  payload: Record<string, unknown> | null,
): string | null => {
  if (!payload) {
    return null;
  }
  const role =
    payload["triedge:role"] ??
    payload["custom:role"] ??
    payload.role;
  return typeof role === "string" && role.trim().length > 0
    ? role.trim()
    : null;
};

const resolveNameFromPayload = (
  payload: Record<string, unknown> | null,
  email: string | null,
): string | null => {
  if (!payload) {
    return email;
  }
  const fullName = payload.name;
  if (typeof fullName === "string" && fullName.trim()) {
    return fullName.trim();
  }
  const givenName =
    typeof payload.given_name === "string" ? payload.given_name : "";
  const familyName =
    typeof payload.family_name === "string" ? payload.family_name : "";
  const combined = `${givenName} ${familyName}`.trim();
  if (combined) {
    return combined;
  }
  return email;
};

const resolvePersonaFromGroups = (
  groups: readonly YstnjCognitoGroup[],
  role: string | null,
): CognitoPersona => {
  const normalizedGroupPersonas = groups
    .map((group) => normalizePersonaCandidate(group))
    .filter(Boolean) as Exclude<CognitoPersona, "guest">[];
  if (normalizedGroupPersonas.includes(YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR)) {
    return YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR;
  }
  if (normalizedGroupPersonas.includes(YSTNJ_RUNTIME_PERSONA.PARENT)) {
    return YSTNJ_RUNTIME_PERSONA.PARENT;
  }
  if (normalizedGroupPersonas.includes(YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR)) {
    return YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR;
  }
  if (normalizedGroupPersonas.includes(YSTNJ_RUNTIME_PERSONA.STAFF)) {
    return YSTNJ_RUNTIME_PERSONA.STAFF;
  }

  if (
    groups.length === 0
    && normalizePersonaCandidate(role) === YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR
  ) {
    return YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR;
  }

  return YSTNJ_RUNTIME_PERSONA.GUEST;
};

const resolveIsSuperAdmin = (
  groups: readonly YstnjCognitoGroup[],
  role: string | null,
): boolean => {
  return (
    groups.includes(YSTNJ_COGNITO_GROUP.SUPER_ADMINISTRATOR)
    || normalizeToken(role) === YSTNJ_COGNITO_GROUP.SUPER_ADMINISTRATOR
  );
};

const clearForeignCognitoTokenFamiliesFromStorage = (
  storage: Storage,
  allowedClientId: string,
): string[] => {
  const removedKeys: string[] = [];
  for (const key of collectStorageKeys(storage)) {
    const clientId = extractClientIdFromStorageKey(key);
    if (!clientId || clientId === allowedClientId) {
      continue;
    }
    storage.removeItem(key);
    removedKeys.push(key);
  }
  return removedKeys;
};

export const clearStaleCognitoTokenFamilies = (): string[] => {
  if (typeof window === "undefined") {
    return [];
  }
  const authority = resolveYstnjAuthAuthority();
  if (!authority.userPoolClientId) {
    return [];
  }

  try {
    return [
      ...clearForeignCognitoTokenFamiliesFromStorage(
        window.localStorage,
        authority.userPoolClientId,
      ),
      ...clearForeignCognitoTokenFamiliesFromStorage(
        window.sessionStorage,
        authority.userPoolClientId,
      ),
    ];
  } catch (error) {
    console.warn("[auth] Unable to clear stale Cognito token families:", error);
    return [];
  }
};

export const resolveCognitoProfileResolution = (): CognitoProfileResolution => {
  const tokenResolution = readIdTokenResolution();
  const payload = tokenResolution.token
    ? decodeJwtPayload(tokenResolution.token)
    : null;
  const groups = resolveGroupsFromPayload(payload);
  const email = resolveEmailFromPayload(payload);
  const username = resolveUsernameFromPayload(payload);
  const name = resolveNameFromPayload(payload, email);
  const provider = resolveProviderFromPayload(payload);
  const emailVerified = payload ? isIntellioEmailVerified(payload) : false;
  const userSub = resolveUserSubFromPayload(payload);
  const permissions = resolvePermissionsFromPayload(payload);
  const role = resolveRoleFromPayload(payload);
  const persona = resolvePersonaFromGroups(groups, role);
  const isIntellioUser = Boolean(
    email && email.toLowerCase().endsWith("@intelliolabs.com"),
  );
  const isSuperAdmin =
    persona === YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR
    && resolveIsSuperAdmin(groups, role);

  return {
    profile: {
      name: name ?? undefined,
      email: email ?? undefined,
      username: username ?? undefined,
      groups,
      permissions,
      role: role ?? undefined,
      persona,
      userSub: userSub ?? undefined,
      provider: provider ?? undefined,
      emailVerified,
      isGoogleProvider: provider?.toLowerCase() === "google",
      isIntellioUser,
      isSuperAdmin,
    },
    expectedTuple: tokenResolution.authority,
    observedTokenTuple: tokenResolution.observedTokenTuple,
    failureReason: tokenResolution.failureReason,
    token: tokenResolution.token,
    tokenKey: tokenResolution.tokenKey,
  };
};

export const resolveCognitoProfile = (): CognitoProfile | null =>
  resolveCognitoProfileResolution().profile;

export const resolveCognitoProfileFirstName = (
  profile: CognitoProfile | null | undefined,
): string | null => {
  const displayName = String(profile?.name ?? "").trim();
  if (!displayName || displayName.includes("@")) {
    return null;
  }
  const [firstName] = displayName.split(/\s+/);
  return firstName?.trim() ? firstName.trim() : null;
};

export const hasCognitoPermission = (
  profile: CognitoProfile | null | undefined,
  permission: string,
): boolean => {
  const normalizedPermission = String(permission ?? "")
    .trim()
    .toLowerCase();
  if (!normalizedPermission) {
    return false;
  }
  return (profile?.permissions ?? []).includes(normalizedPermission);
};

export const canUseShellThemeToggle = (
  profile: CognitoProfile | null | undefined,
): boolean => {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin === true) {
    return true;
  }
  return (
    profile.persona === YSTNJ_RUNTIME_PERSONA.STAFF
    || profile.persona === YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR
    || profile.persona === YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR
    || profile.persona === YSTNJ_RUNTIME_PERSONA.PARENT
  );
};

export const resolveStudentsFamiliesEntryPath = (
  persona: CognitoPersona,
): string => {
  if (persona === YSTNJ_RUNTIME_PERSONA.PARENT) {
    return "/school/students/dashboard";
  }
  if (
    persona === YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR
    || persona === YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR
    || persona === YSTNJ_RUNTIME_PERSONA.STAFF
  ) {
    return "/school/students/dashboard";
  }
  return "/school/students/dashboard";
};

export const resolveAppShellPersonaLabel = (
  persona: CognitoPersona,
  defaultLabel: string,
  options?: {
    isSuperAdmin?: boolean;
  },
): string => {
  if (
    options?.isSuperAdmin
    || persona === YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR
  ) {
    return "Super Administrator";
  }
  if (persona === YSTNJ_RUNTIME_PERSONA.PARENT) {
    return "Parent";
  }
  if (persona === YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR) {
    return "Administrator";
  }
  if (persona === YSTNJ_RUNTIME_PERSONA.STAFF) {
    return "Staff";
  }
  return defaultLabel;
};
